import { useCallback, useContext, useEffect, useRef } from "react";
import { InfiniteListContext } from "context/InfiniteList/Context";

const FETCH_THRESHOLD = "400px";

interface UseInfiniteListReturn<T> {
    displayedItems: T[];
    isFullShimmering: boolean;
    isLoadMoreShimmering: boolean;
    hasMore: boolean;
    sentinelRef: React.RefObject<HTMLDivElement>;
    handleResetFilters: () => void;
}

/**
 * Drives infinite-scroll pagination on top of InfiniteListProvider.
 */
function useInfiniteList<T>(): UseInfiniteListReturn<T> {
    const {
        items,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        changeFetcherParams,
        requestedPerPage,
        defaultFetcherParams,
    } = useContext(InfiniteListContext);

    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    void fetchNextPage();
                }
            },
            { rootMargin: FETCH_THRESHOLD },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleResetFilters = useCallback(() => {
        changeFetcherParams({
            ...defaultFetcherParams,
            page: 1,
            per_page: requestedPerPage,
        });
    }, [changeFetcherParams, defaultFetcherParams, requestedPerPage]);

    return {
        displayedItems: items as T[],
        isFullShimmering: isLoading && !isFetchingNextPage,
        isLoadMoreShimmering: isFetchingNextPage,
        hasMore: hasNextPage,
        sentinelRef,
        handleResetFilters,
    };
}

export default useInfiniteList;
